import React, { useState } from "react";
import DefaultSearchSelectInput from "../../inputs/defaultSearchSelectInput";
import DefaultInput from "../../inputs/defaultInput";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { Tooltip } from "flowbite-react";

const PAYROLL_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "weekly", label: "Weekly" },
  { value: "custom", label: "Custom" },
];

const PayrollCycleStep = ({ value, onChange }) => {
  const [cycleType, setCycleType] = useState(value?.type || null);
  const [day, setDay] = useState(value?.day || "");
  const [customDays, setCustomDays] = useState(value?.customDays || "");

  const handleCycleTypeChange = (selected) => {
    const newType = selected?.value || null;
    setCycleType(newType);
    const newValue = {
      type: newType,
      day: newType === "custom" ? null : day,
      customDays: newType === "custom" ? customDays : null,
    };
    onChange(newValue);
  };

  const handleDayChange = (e) => {
    const newDay = e.target.value;
    setDay(newDay);
    onChange({
      type: cycleType,
      day: newDay,
      customDays: null,
    });
  };

  const handleCustomDaysChange = (e) => {
    const newDays = e.target.value;
    setCustomDays(newDays);
    onChange({
      type: cycleType,
      day: null,
      customDays: newDays,
    });
  };

  const getSummaryText = () => {
    if (!cycleType) return null;
    if (cycleType === "custom") {
      return `Custom — Every ${customDays || "N"} days`;
    }
    const dayText = day ? ` — Day ${day}` : "";
    return `${PAYROLL_OPTIONS.find((o) => o.value === cycleType)?.label}${dayText}`;
  };

  return (
    <div className="flex flex-col gap-6" role="region" aria-labelledby="payroll-cycle-heading">
      <div>
        <h2 id="payroll-cycle-heading" className="text-xl font-semibold text-theme mb-2">
          Payroll Cycle Configuration
        </h2>
        <p className="text-sm text-textLight">
          Configure how often payroll will be processed for your organization. This configuration will be used when creating payroll runs.
        </p>
        <p className="text-xs text-textLight mt-1 italic">
          Note: Payroll cycles are managed in the Payroll Management section. This step helps you understand your payroll frequency.
        </p>
      </div>

      <div className="flex flex-col gap-4 w-1/3">
        <DefaultSearchSelectInput
          label="Payroll Cycle"
          required
          colSpan={4}
          value={
            cycleType
              ? PAYROLL_OPTIONS.find((o) => o.value === cycleType)
              : null
          }
          onChange={handleCycleTypeChange}
          options={PAYROLL_OPTIONS}
          info="Select the frequency at which payroll will be processed"
        />

        {cycleType === "monthly" && (
          <DefaultInput
            label="Payroll Day"
            type="number"
            placeholder="Enter day of month (1-31)"
            value={day}
            onChange={handleDayChange}
            min="1"
            max="31"
            required
            info="Day of the month when payroll will be processed"
          />
        )}

        {cycleType === "biweekly" && (
          <DefaultInput
            label="Starting Day"
            type="number"
            placeholder="Enter starting day (1-14)"
            value={day}
            onChange={handleDayChange}
            min="1"
            max="14"
            required
            info="Starting day of the bi-weekly cycle"
          />
        )}

        {cycleType === "weekly" && (
          <DefaultSearchSelectInput
            label="Payroll Day"
            required
            value={
              day
                ? [
                    { value: "monday", label: "Monday" },
                    { value: "tuesday", label: "Tuesday" },
                    { value: "wednesday", label: "Wednesday" },
                    { value: "thursday", label: "Thursday" },
                    { value: "friday", label: "Friday" },
                    { value: "saturday", label: "Saturday" },
                    { value: "sunday", label: "Sunday" },
                  ].find((o) => o.value === day)
                : null
            }
            onChange={(selected) => {
              setDay(selected?.value || "");
              onChange({
                type: cycleType,
                day: selected?.value || "",
                customDays: null,
              });
            }}
            options={[
              { value: "monday", label: "Monday" },
              { value: "tuesday", label: "Tuesday" },
              { value: "wednesday", label: "Wednesday" },
              { value: "thursday", label: "Thursday" },
              { value: "friday", label: "Friday" },
              { value: "saturday", label: "Saturday" },
              { value: "sunday", label: "Sunday" },
            ]}
            info="Day of the week when payroll will be processed"
          />
        )}

        {cycleType === "custom" && (
          <DefaultInput
            label="Number of Days"
            type="number"
            placeholder="Enter number of days"
            value={customDays}
            onChange={handleCustomDaysChange}
            min="1"
            required
            info="Number of days between each payroll cycle"
          />
        )}
      </div>

      {/* Summary Card */}
      {cycleType && (
        <div className="bg-information-50 border border-information-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <IoIosInformationCircleOutline className="text-information-600 text-xl flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-information-900 mb-1">
                Payroll Cycle Summary
              </p>
              <p className="text-sm text-information-700">
                {getSummaryText()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollCycleStep;

